/**
 * Plain-language reasons behind a license refusal, for every producer. The
 * code is what the gate answered with: the v2 console writes
 * `license_locked` when a person clicks a plan-locked surface (it knows
 * only that the entitlement rule said no); the MCP servers write
 * `license_` + the ReasonCode of mcp.Explain_Acc_UserFeatureAccess (its
 * header lists them), `license_denied` when no explanation was available,
 * or an account-level reason. Unknown codes render as-is so a new reason
 * never shows blank.
 */
const LICENSE_DENIED_REASON_LABELS: Record<string, string> = {
  license_locked: "Not in the customer's packages",
  license_no_license: "No license for the product",
  license_license_not_live: "License not live (ended trial or expired)",
  license_not_named: "Not a named seat on the license",
  license_addon_missing: "Add-on missing",
  license_license_without_feature: "License without the package",
  license_no_customer: "User has no customer",
  license_feature_not_sold: "Not sold in any package",
  license_feature_unknown: "Unknown feature code",
  license_no_user: "No Fusion account",
  license_rule_refused: "Refused by the entitlement rule",
  license_denied: "License refused",
  account_unknown: "No Fusion account",
  account_blocked: "Account blocked",
  account_deleted: "Account deleted",
}

export function formatLicenseDeniedReason(code: string | null | undefined) {
  if (!code) return "Refused"
  return LICENSE_DENIED_REASON_LABELS[code] ?? code
}

/** How many features / people the lists show before "more". */
export const LICENSE_DENIED_FEATURE_LIST_LIMIT = 6
export const LICENSE_DENIED_PORTFOLIO_LIST_LIMIT = 10

/** Plain-language explanation behind the "blocked by license" sections. */
export const LICENSE_DENIED_SECTION_TOOLTIP =
  "Times someone tried to use a feature their license does not carry and the " +
  "license rule said no: a click on a locked surface in the console (the " +
  "upgrade dialog opened), or an MCP sign-in the server refused. Counted per " +
  "UTC day from the same tracking events as usage, never as usage itself. " +
  "An AI client retries on its own, so MCP attempts say how hard someone " +
  "tried, not how many questions they asked. People trying is demand: the " +
  "upsell conversation to open."
